import { z } from 'zod'

export const uuidSchema = z.uuid()
export type Uuid = z.infer<typeof uuidSchema>

/**
 * Money and material quantities are `numeric` in Postgres and travel as strings, never as
 * JS numbers. `1200.500 + (-300.250)` must equal exactly `900.250`; binary floating point
 * cannot promise that, and this system reconciles invoices against payments.
 */
export const decimalSchema = z
  .string()
  .regex(/^-?\d+(\.\d+)?$/, 'must be a decimal string, e.g. "1200.500"')

const isPositiveDecimal = (value: string) =>
  !value.startsWith('-') && value.replace('.', '').replace(/^0+/, '').length > 0

export const positiveDecimalSchema = decimalSchema.refine(
  isPositiveDecimal,
  'Значення має бути більшим за нуль',
)

/** PostgreSQL money columns are numeric(18,2); reject values that would otherwise round. */
export const moneySchema = decimalSchema.refine(
  (value) => (value.split('.')[1]?.length ?? 0) <= 2,
  'Сума може містити не більше двох знаків після коми',
)
export const positiveMoneySchema = moneySchema.refine(
  isPositiveDecimal,
  'Значення має бути більшим за нуль',
)

export type Decimal = z.infer<typeof decimalSchema>

/** Ukrainian keyboards and habits type a decimal comma; the wire format needs a dot. */
export const normalizeDecimalInput = (value: string): string => value.trim().replace(',', '.')

export const CONTACT_PHONE_MAX_LENGTH = 30
export const CONTACT_EMAIL_MAX_LENGTH = 254

export const contactPhoneSchema = z
  .string()
  .trim()
  .max(
    CONTACT_PHONE_MAX_LENGTH,
    `Телефон має містити не більше ${CONTACT_PHONE_MAX_LENGTH} символів`,
  )
  .refine(
    (value) => /^\+?[\d\s()-]+$/.test(value),
    'Телефон може містити лише цифри, +, пробіли, дужки й дефіси',
  )
  .refine((value) => {
    const digitCount = value.replace(/\D/g, '').length
    return digitCount >= 7 && digitCount <= 15
  }, 'Телефон має містити від 7 до 15 цифр')

export const contactEmailSchema = z
  .string()
  .trim()
  .max(CONTACT_EMAIL_MAX_LENGTH, `Email має містити не більше ${CONTACT_EMAIL_MAX_LENGTH} символів`)
  .refine((value) => z.email().safeParse(value).success, 'Вкажіть коректну email-адресу')

/** All timestamps are timestamptz and serialise as ISO 8601. Business timezone: Europe/Kyiv. */
export const timestampSchema = z.iso.datetime({ offset: true })

export const auditFieldsSchema = z.object({
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
})

export type AuditFields = z.infer<typeof auditFieldsSchema>
